import { z } from 'zod';

/* Defensive coercion — see saleStockSchemas note. */
const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);

/* Inwards DC summary (card 9391). Grain: delivery challan. brand_names is the
   vendor's comma-joined brand list from stg_wms_vendor — the UI rolls DCs up
   by this label (splitting would double-count a multi-brand vendor's qty). */
export const OpsInwardsDcSchema = z.array(
  z.object({
    dc_id: num(),
    reference_number: z.string().nullable(),
    dc_status: z.string().nullable(),
    brand_names: z.string().nullable(),
    vendor_name: z.string().nullable(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    received_date: z.string().nullable(),
    received_qty: num(),
    /* Units on the DC (enabled item rows). */
    dc_qty: num(),
    grn_received_qty: num(),
    grn_good_qty: num(),
    qc_fail_qty: num(),
    pending_grn_qty: num(),
    /* DC header total_correction_quantity while SENT_FOR_CORRECTION, else 0. */
    correction_qty: num(),
    /* When the DC entered its current status (null if no audit history). */
    status_since: z.string().nullable(),
    /* When the DC was uploaded (created). */
    uploaded_at: z.string().nullable(),
    /* Raw arrival time; null until stock is physically received (received_date
       falls back to the upload date for the window filter). */
    stock_received_at: z.string().nullable(),
    /* Assigned cataloguer's name; null if unassigned. */
    cataloguer_name: z.string().nullable(),
  }),
);
export type OpsInwardsDc = z.infer<typeof OpsInwardsDcSchema>;

/* Pending-for-GRN DC backlog (card 9398) — current state, no date window:
   pre-GRN statuses (EDITING / READY_FOR_GRN / SENT_FOR_CORRECTION) or
   received > GRNed, with days pending since stock arrived. */
export const OpsPendingDcSchema = z.array(
  z.object({
    dc_id: num(),
    reference_number: z.string().nullable(),
    dc_status: z.string().nullable(),
    brand_names: z.string().nullable(),
    vendor_name: z.string().nullable(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    created_date: z.string().nullable(),
    received_date: z.string().nullable(),
    received_qty: num(),
    grn_received_qty: num(),
    grn_good_qty: num(),
    qc_fail_qty: num(),
    pending_grn_qty: num(),
    correction_qty: num(),
    dc_qty: num(),
    dc_items_total: num(),
    days_since_upload: num(),
    days_pending: num(),
    status_since: z.string().nullable(),
    /* Arrival timestamp; null until stock is physically received. */
    stock_received_at: z.string().nullable(),
    /* Assigned cataloguer's name; null if unassigned. */
    cataloguer_name: z.string().nullable(),
  }),
);
export type OpsPendingDc = z.infer<typeof OpsPendingDcSchema>;

/* DC stage timeline (card 10096), one DC per query. STATUS rows are status
   changes (consecutive repeats collapsed), ordered by revision time;
   MILESTONE rows are point events (STOCK_RECEIVED = arrived_on). */
export const OpsDcTimelineSchema = z.array(
  z.object({
    event_type: z.enum(['STATUS', 'MILESTONE']),
    event: z.string(),
    event_at: z.string(),
  }),
);
export type OpsDcTimeline = z.infer<typeof OpsDcTimelineSchema>;

/* Gatepasses of one GPO with each step's timestamp (card 10285). */
export const OpsGpoGatepassTimelineSchema = z.array(
  z.object({
    gatepass_code: z.string(),
    manifest_code: z.string().nullable(),
    manifest_status: z.string().nullable(),
    scanned_qty: num(),
    inwarded_qty: num(),
    created_at: z.string().nullable(),
    scanning_started_at: z.string().nullable(),
    scanned_at: z.string().nullable(),
    in_transit_at: z.string().nullable(),
    received_at: z.string().nullable(),
    inwarding_started_at: z.string().nullable(),
    closed_at: z.string().nullable(),
  }),
);
export type OpsGpoGatepassTimeline = z.infer<typeof OpsGpoGatepassTimelineSchema>;

/* GPO performance (card 10284; was 9392). Grain: (brand, from, to, gatepass_order_code)
   — a GPO spanning brands appears once per brand with that brand's items. */
export const OpsGpoSchema = z.array(
  z.object({
    brand_name: z.string(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    from_facility_name: z.string().nullable(),
    to_facility_name: z.string().nullable(),
    gatepass_order_code: z.string().nullable(),
    requested_date: z.string().nullable(),
    transfer_type: z.string().nullable(),
    requested_qty: num(),
    scanned_qty: num(),
    inwarded_qty: num(),
    short_qty: num(),
    pending_scan_qty: num(),
    pending_inward_qty: num(),
    rts_qty: num(),
    scan_delayed_qty: num(),
    inward_delayed_qty: num(),
    max_scan_delay_days: num(),
    max_inward_delay_days: num(),
    gpo_status: z.string(),
    /* GPO Tracker (card 10284). */
    gatepass_order_id: numNull(),
    wms_gpo_status: z.string().nullable(),
    stn_created_by: z.string().nullable(),
    stn_request_number: z.string().nullable(),
    use_case: z.string().nullable(),
    leg_number: numNull(),
    journey_code: z.string().nullable(),
    scanning_started_on: z.string().nullable(),
    last_in_transit_on: z.string().nullable(),
    last_received_on: z.string().nullable(),
    in_transit_qty: num(),
    arrived_qty: num(),
    short_received_qty: num(),
    live_lines: num(),
    open_lines: num(),
    stage_rank: num(),
  }),
);
export type OpsGpo = z.infer<typeof OpsGpoSchema>;

/* Live stock by brand × facility (card 9393). STORE + WAREHOUSE only. */
export const OpsStockLocationSchema = z.array(
  z.object({
    brand_name: z.string(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    facility_name: z.string(),
    facility_type: z.string().nullable(),
    total_stock: num(),
    front_stock: num(),
    unique_styles: num(),
    front_styles: num(),
    avg_size_depth: numNull(),
  }),
);
export type OpsStockLocation = z.infer<typeof OpsStockLocationSchema>;

/* Per brand × facility extras for the location table (card 9400): incoming
   in-transit qty (RETURN_AWAITED stock transfers headed to that facility,
   unicom destination names mapped to facility names) and unicom bad stock. */
// Only this location-detail contract preserves invalid cells as unavailable.
// Validate decimal numeric strings before conversion; never coerce booleans/null.
const stockLocationQuantity = (nonnegative = false) =>
  z.unknown().transform((value): number | null => {
    if (typeof value === 'string') {
      const text = value.trim();
      if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(text)) return null;
      value = Number(text);
    }
    return typeof value === 'number' && Number.isFinite(value) && (!nonnegative || value >= 0)
      ? value
      : null;
  });

export const OpsStockLocationExtrasSchema = z.array(
  z.object({
    brand_name: z.string(),
    facility_name: z.string(),
    incoming_qty: stockLocationQuantity(),
    outgoing_qty: stockLocationQuantity(),
    bad_qty: stockLocationQuantity(true),
  }),
);
export type OpsStockLocationExtras = z.infer<typeof OpsStockLocationExtrasSchema>;

/* Live stock ageing (card 9394). Grain: (brand, age bucket, first-GRN month).
   Buckets and months are independent rollups of the same rows. */
export const OpsStockAgeingSchema = z.array(
  z.object({
    brand_name: z.string(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    age_bucket: z.enum(['fresh', 'd30_90', 'd90_180', 'd180_plus', 'unknown']),
    received_month: z.string().nullable(),
    qty: num(),
    qty_age_days: num(),
  }),
);
export type OpsStockAgeing = z.infer<typeof OpsStockAgeingSchema>;

/* Live stock velocity (card 9395). Style-level classification off last-28-day
   sales rolled to brand: fast ≤4 wks cover · regular ≤12 · slow >12 · dead =
   stocked but zero sales in 28 days. */
export const OpsStockVelocitySchema = z.array(
  z.object({
    brand_name: z.string(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    velocity: z.enum(['fast', 'regular', 'slow', 'dead']),
    qty: num(),
    styles: num(),
    sold_28d: num(),
  }),
);
export type OpsStockVelocity = z.infer<typeof OpsStockVelocitySchema>;

/* Stock reconciliation by brand (card 9396). Mirrors card 6227 on dashboard
   176 "Brand Inventory Workhorse" (same exclusions + derived measures):
   expected = GRN − sold − RTV · physical = unicom good+bad+in-transit + HQ ·
   expected physical = expected − lost. Adds RTV-pending and BM/portfolio. */
export const OpsStockReconSchema = z.array(
  z.object({
    brand_name: z.string(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    grn_qty: num(),
    sold_qty: num(),
    rtv_qty: num(),
    expected_qty: num(),
    good_qty: num(),
    bad_qty: num(),
    in_transit_qty: num(),
    hq_qty: num(),
    physical_qty: num(),
    lost_qty: num(),
    expected_physical_qty: num(),
    rtv_pending_qty: numNull(),
    /* Physical good + bad stock at Samalkha RTV; optional for mixed card rollouts. */
    rtv_hub_qty: num().nullable().optional(),
  }),
);
export type OpsStockRecon = z.infer<typeof OpsStockReconSchema>;

/* Style-grain action matrix (card 9402): live stock (STORE+WAREHOUSE, excl
   Dwarka) with first-GRN age and last-28d sales. Velocity/quadrant/cover are
   derived client-side. */
export const OpsActionMatrixSchema = z.array(
  z.object({
    lk_style_number: z.string(),
    brand_name: z.string(),
    brand_manager: z.string().nullable(),
    merch_portfolio: z.string().nullable(),
    product_name: z.string().nullable(),
    image: z.string().nullable(),
    age_days: numNull(),
    sold_28d: num(),
    qty: num(),
    store_qty: numNull(),
    wh_qty: numNull(),
  }),
);
export type OpsActionMatrix = z.infer<typeof OpsActionMatrixSchema>;

/* SKU-grain companion (card 9403) — lazy-loaded only when exporting
   SKU/qty-level replen or RTV lists. */
export const OpsSkuExportSchema = z.array(
  z.object({
    sku_code: z.string(),
    size: z.string().nullable(),
    color: z.string().nullable(),
    lk_style_number: z.string(),
    product_name: z.string().nullable(),
    brand_name: z.string(),
    age_days: numNull(),
    style_sold_28d: num(),
    sku_sold_28d: num(),
    qty: num(),
    store_qty: numNull(),
    wh_qty: numNull(),
  }),
);
export type OpsSkuExport = z.infer<typeof OpsSkuExportSchema>;
